import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "../styles.modules.scss";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "../../../button";
import { useElementOnScreen } from "../../../../hooks/use-element-on-screen";
import { useSendCommand } from "../../../../hooks/use-send-command";
import { useOpenNewsArticle } from "../../../../hooks/use-open-news-article";
import { handleCTAClick } from "..";
import { parseCTAs } from "../parse-ctas";
import { usePollContext } from "../../../../stores/poll";
import {
    findRelatedMediaByTitle,
    findRelatedMediaLabelByTitle,
} from "../../../../utils/related-media";
import classNames from "classnames";
import { bridge } from "../../../../utils/bridge";
import { Timer } from "../campaign-parts/timer";
import { getLocalizedShortenedNumber } from "inbox-contentful/src/localizedShortenedNumber";
import { Locale } from "inbox-contentful";

export type ProgressSlideType = {
    type: "progressTrackerEntry";
    id: string;
    title: string;
    tracker: ProgressTracker;
    milestones: ProgressMilestone[];
    ctas: InboxCTA[];
    targeting?: Targeting;
    lang: string;
    relatedMedia?: RelatedMedia[];
};

export const MangoParadoxProgressSlide = ({
    slide,
    previousDaySlide,
    allRewards,
    pickedRewards,
    pickedSpellFactoryRewards,
}: {
    slide: ProgressSlideType;
    previousDaySlide?: ProgressSlideType;
    allRewards: { [key: number]: (string | undefined)[] };
    pickedRewards: { [key: number]: number | undefined };
    pickedSpellFactoryRewards: { [key: number]: string | undefined };
}) => {
    const { title, tracker, milestones, targeting, relatedMedia } = slide;

    const [ref, isVisible] = useElementOnScreen<HTMLDivElement>({
        threshold: 0.001,
    });
    const bottomElementRef = React.useRef<HTMLDivElement>(null);
    const firstLockedRef = React.useRef<HTMLDivElement>(null);
    const trackerProgressRef = React.useRef<HTMLDivElement>(null);
    const sendCommand = useSendCommand();
    const openNewsArticle = useOpenNewsArticle();
    const intl = useIntl();
    const {
        actions: { openModal },
    } = usePollContext();

    const dayNumber = Number(
        relatedMedia?.find((media) => media.title === "day-number")?.label ?? 0,
    );

    const hasSeenBefore =
        localStorage.getItem(`mango-paradox-seen-day-${dayNumber}`) === "true";

    const spellFactoryRewardClaimed =
        localStorage.getItem(
            `mango-paradox-spell-factory-reward-claimed-day-${dayNumber}`,
        ) === "true";

    const [startSequencePhase, setStartSequencePhase] = useState<
        "start" | "playing" | "end"
    >(
        spellFactoryRewardClaimed && !hasSeenBefore
            ? "playing"
            : dayNumber === 1 || previousDaySlide === undefined || hasSeenBefore
              ? "end"
              : "start",
    );

    const [previousRewardsIsOpen, setPreviousRewardsIsOpen] = useState(false);
    const togglePreviousRewardsIsOpen = () => {
        setPreviousRewardsIsOpen((prev) => !prev);
    };

    useEffect(() => {
        // Clean up old localStorage items from previous events, to not clutter localStorage
        // and potentially hit storage limits

        const localStorageItems = Object.keys(localStorage).filter(
            (key) =>
                key.startsWith("spire-crypt") ||
                key.startsWith("echo-vault") ||
                key.startsWith("golden-ripple"),
        );

        if (localStorageItems.length === 0) return;

        localStorageItems.forEach((key) => {
            localStorage.removeItem(key);
        });
    }, []);

    useEffect(() => {
        const spellFactoryReward = relatedMedia?.find(
            (media) => media.title === "previous-day-spell-factory-reward",
        )?.label;

        const spellFactoryRewardClaimed =
            localStorage.getItem(
                `mango-paradox-spell-factory-reward-claimed-day-${dayNumber}`,
            ) === "true";

        if (
            !hasSeenBefore &&
            spellFactoryRewardClaimed &&
            spellFactoryReward !== undefined
        ) {
            setTimeout(() => {
                setStartSequencePhase("end");
                localStorage.setItem(
                    `mango-paradox-seen-day-${dayNumber}`,
                    "true",
                );
            }, 1000);
        }

        if (
            isVisible &&
            startSequencePhase === "start" &&
            !spellFactoryRewardClaimed
        ) {
            setTimeout(() => {
                setStartSequencePhase("playing");
            }, 1000);

            if (spellFactoryReward === undefined || spellFactoryRewardClaimed) {
                setTimeout(() => {
                    setStartSequencePhase("end");
                    localStorage.setItem(
                        `mango-paradox-seen-day-${dayNumber}`,
                        "true",
                    );
                }, 4000);
            }
        }
    }, [startSequencePhase, isVisible, relatedMedia, hasSeenBefore]);

    const handleClick = handleCTAClick(
        sendCommand,
        openNewsArticle,
        openModal,
        title,
    );

    const relatedMediaStyles = useMemo(() => {
        const styles: React.CSSProperties & { [key: string]: string } = {};

        const imageMap = {
            icon: "--icon-image",
            "modifier-icon": "--modifier-icon",
            "background-layer": "--background-layer-image",
            ray: "--ray",
            lock: "--lock",
            "reward-1": "--reward-1-image",
            "reward-2": "--reward-2-image",
            "reward-3": "--reward-3-image",
        };

        const labelMap = {
            "background-layer": "--background-color",
        };

        Object.entries(imageMap).forEach(([title, cssVar]) => {
            const imageUrl = findRelatedMediaByTitle(relatedMedia ?? [], title);
            if (imageUrl) styles[cssVar] = `url("${imageUrl}")`;
        });

        Object.entries(labelMap).forEach(([title, cssVar]) => {
            const label = findRelatedMediaLabelByTitle(
                relatedMedia ?? [],
                title,
            );
            if (label) styles[cssVar] = label;
        });

        return styles;
    }, [relatedMedia]);

    const previousDayRelatedMediaStyles = useMemo(() => {
        const styles: React.CSSProperties & { [key: string]: string } = {};

        const imageMap = {
            icon: "--previous-day-icon-image",
            "background-layer": "--previous-day-background-layer-image",
            "reward-1": "--previous-day-reward-1-image",
            "reward-2": "--previous-day-reward-2-image",
            "reward-3": "--previous-day-reward-3-image",
            "modifier-icon": "--previous-day-modifier-icon",
            ray: "--previous-day-ray",
        };

        const labelMap = {
            "background-layer": "--previous-day-background-color",
        };

        Object.entries(imageMap).forEach(([title, cssVar]) => {
            const imageUrl = findRelatedMediaByTitle(
                previousDaySlide?.relatedMedia ?? [],
                title,
            );
            if (imageUrl) styles[cssVar] = `url("${imageUrl}")`;
        });

        Object.entries(labelMap).forEach(([title, cssVar]) => {
            const label = findRelatedMediaLabelByTitle(
                previousDaySlide?.relatedMedia ?? [],
                title,
            );
            if (label) styles[cssVar] = label;
        });

        return styles;
    }, [previousDaySlide?.relatedMedia]);

    React.useEffect(() => {
        if (bottomElementRef.current && isVisible) {
            const top = bottomElementRef.current.offsetTop;
            ref.current?.parentElement?.scrollTo({
                top,
            });
        }
    }, [bottomElementRef.current, isVisible]);

    //

    React.useEffect(() => {
        setTimeout(() => {
            if (firstLockedRef.current && isVisible) {
                // Scroll the top of the filled bar to the center of the view
                const trackerProgressOffsetTop =
                    trackerProgressRef.current?.offsetTop ?? 0;
                const trackersOffsetTop =
                    firstLockedRef.current.parentElement?.parentElement
                        ?.offsetTop ?? 0;
                const trackersMarginTop = firstLockedRef.current.parentElement
                    ?.parentElement
                    ? parseFloat(
                          getComputedStyle(
                              firstLockedRef.current.parentElement
                                  ?.parentElement,
                          ).marginTop,
                      )
                    : 0;
                const contentPaddingTop = firstLockedRef.current.parentElement
                    ?.parentElement?.parentElement
                    ? parseFloat(
                          getComputedStyle(
                              firstLockedRef.current.parentElement
                                  ?.parentElement?.parentElement,
                          ).paddingTop,
                      )
                    : 0;

                const top =
                    trackerProgressOffsetTop +
                    trackersOffsetTop +
                    trackersMarginTop +
                    contentPaddingTop -
                    (ref.current?.parentElement?.clientHeight ?? 0) / 1.5;

                ref.current?.parentElement?.scrollTo({
                    top,
                    behavior: "smooth",
                });
            }
        }, 500);
    }, [bottomElementRef.current, isVisible]);

    const timerString =
        startSequencePhase === "start" || startSequencePhase === "playing"
            ? previousDaySlide?.relatedMedia?.find(
                  (media) => media.title === "timer",
              )?.label
            : relatedMedia?.find((media) => media.title === "timer")?.label;

    const ctas = parseCTAs(slide.ctas, handleClick);

    const fillPercentage = tracker.progress;
    const barColor = tracker.color;

    const style = {
        width: `${Math.max(fillPercentage, 1.25)}%`,
    };

    const iconStyle = {
        width: `${Math.max(fillPercentage, 1.25)}%`,
    };

    const previousDayStyle = {
        width: `${Math.max(previousDaySlide?.tracker?.progress ?? 0, 1.25)}%`,
    };

    const midWayStyle = {
        width: "1.25%",
    };

    const hasMileStoneLabels = milestones.some(
        (milestone) => !!milestone.label,
    );

    const barStyle = {
        "--bar-color": barColor,
        "--previous-day-bar-color": previousDaySlide?.tracker.color ?? barColor,
        height: "100%",
        ...relatedMediaStyles,
        ...previousDayRelatedMediaStyles,
    } as React.CSSProperties;

    const previousDaySpellFactoryReward = relatedMedia?.find(
        (media) => media.title === "previous-day-spell-factory-reward",
    )?.label;

    const translate = (
        id:
            | "previousRewards"
            | "dayReward"
            | "noReward"
            | "day"
            | "watch"
            | "moreInfo",
        dayNumber: number,
    ) => {
        type Translations = {
            previousRewards: string;
            dayReward: string;
            noReward: string;
            day: string;
            watch: string;
            moreInfo: string;
        };

        const translations: {
            [key: string]: Translations;
        } = {
            de: {
                previousRewards: "Vorherige Belohnungen",
                dayReward: `Belohnung – Tag ${dayNumber - 1}`,
                noReward: "Keine Belohnung",
                day: `Tag ${dayNumber}`,
                watch: "Ansehen",
                moreInfo: "Mehr Infos",
            },
            en: {
                previousRewards: "Previous Rewards",
                dayReward: `Day ${dayNumber - 1} Reward`,
                noReward: "No Reward",
                day: `Day ${dayNumber}`,
                watch: "Watch",
                moreInfo: "More Info",
            },
            es: {
                previousRewards: "Recompensas anteriores",
                dayReward: `Recompensa del día ${dayNumber - 1}`,
                noReward: "Ninguna recompensa",
                day: `Día ${dayNumber}`,
                watch: "Ver",
                moreInfo: "Más info.",
            },
            fr: {
                previousRewards: "Récompenses précédentes",
                dayReward: `Récompense du jour ${dayNumber - 1}`,
                noReward: "Aucune récompense",
                day: `Jour ${dayNumber}`,
                watch: "Regarder",
                moreInfo: "Plus d'infos",
            },
            it: {
                previousRewards: "Premi precedenti",
                dayReward: `Premio del ${dayNumber - 1}º giorno`,
                noReward: "Nessuna ricompensa",
                day: `Giorno ${dayNumber}`,
                watch: "Guarda",
                moreInfo: "Altre info",
            },
            ja: {
                previousRewards: "前の報酬",
                dayReward: `${dayNumber - 1}日目の報酬`,
                noReward: "報酬なし",
                day: `${dayNumber}日目`,
                watch: "見る",
                moreInfo: "詳細",
            },
            ko: {
                previousRewards: "이전 보상",
                dayReward: `${dayNumber - 1}일차 보상`,
                noReward: "보상 없음",
                day: `${dayNumber}일차`,
                watch: "보러 가기",
                moreInfo: "추가 정보",
            },
            pl: {
                previousRewards: "Poprzednie nagrody",
                dayReward: `Nagroda za dzień ${dayNumber - 1}`,
                noReward: "Brak nagrody",
                day: `Dzień ${dayNumber}`,
                watch: "Obejrzyj",
                moreInfo: "Więcej informacji",
            },
            pt: {
                previousRewards: "Recompensas anteriores",
                dayReward: `Recompensa do dia ${dayNumber - 1}`,
                noReward: "Nenhuma recompensa",
                day: `Dia ${dayNumber}`,
                watch: "Assistir",
                moreInfo: "Mais informações",
            },
            ru: {
                previousRewards: "Предыдущие награды",
                dayReward: `Награда ${dayNumber - 1}-го дня`,
                noReward: "Нет награды",
                day: `день ${dayNumber}`,
                watch: "Смотреть",
                moreInfo: "Подробнее",
            },
            tr: {
                previousRewards: "Önceki Ödüller",
                dayReward: `${dayNumber - 1}. Gün Ödülü`,
                noReward: "Ödül yok",
                day: `${dayNumber}. gün`,
                watch: "İzle",
                moreInfo: "Daha Fazla Bilgi",
            },
            "zh-hans": {
                previousRewards: "先前奖励",
                dayReward: `第${dayNumber - 1}天奖励`,
                noReward: "无奖励",
                day: `第${dayNumber}天`,
                watch: "观看",
                moreInfo: "更多信息",
            },
            zh: {
                previousRewards: "先前獎勵",
                dayReward: `第${dayNumber - 1}天獎勵`,
                noReward: "無獎勵",
                day: `第${dayNumber}天`,
                watch: "觀看",
                moreInfo: "更多資訊",
            },
        };

        if (translations[slide.lang] === undefined) {
            return translations["en"]?.[id];
        }

        return translations[slide.lang]?.[id];
    };

    const formattedModifierText = (id: string) => {
        const text = intl.formatMessage({ id });

        if (id === "globalText.langMangoParadoxModifierDay2") {
            if (slide.lang === "en") {
                const words = text.split(" ");

                return (
                    <>
                        {words[0]}&nbsp;{words.slice(1).join(" ")}
                    </>
                );
            }

            if (slide.lang === "ja" || slide.lang === "zh") {
                const middleIndex = Math.floor(text.length / 2);

                return (
                    <>
                        {text.slice(0, middleIndex)}
                        <br />
                        {text.slice(middleIndex)}
                    </>
                );
            }

            if (slide.lang === "pt") {
                const words = text.split(" ");

                return (
                    <>
                        {words[0]}
                        <br />
                        {words[1]}
                    </>
                );
            }

            if (slide.lang === "es") {
                const words = text.split(" ");

                return (
                    <>
                        {words.slice(0, -1).join("\u00A0")}
                        <br />
                        {words[words.length - 1]}
                    </>
                );
            }

            if (slide.lang === "tr") {
                const words = text.split(" ");

                return (
                    <>
                        {words.slice(0, -1).join(" ") +
                            "\u00A0" +
                            words[words.length - 1]}
                    </>
                );
            }
        }

        if (id === "globalText.langMangoParadoxModifierDay3") {
            if (
                slide.lang === "en" ||
                slide.lang === "fr" ||
                slide.lang === "it"
            ) {
                const words = text.split(" ");

                return (
                    <>
                        {words.slice(0, -1).join("\u00A0") +
                            " " +
                            words[words.length - 1]}
                    </>
                );
            }

            if (slide.lang === "de") {
                const words = text.split(" ");

                return (
                    <>
                        {words.slice(0, 3).join("\u00A0") +
                            " " +
                            words.slice(3).join("\u00A0")}
                    </>
                );
            }

            if (slide.lang === "zh") {
                const middleIndex = 8;

                return (
                    <>
                        {text.slice(0, middleIndex)}
                        <br />
                        {text.slice(middleIndex)}
                    </>
                );
            }

            if (slide.lang === "ja") {
                const middleIndex = 7;

                return (
                    <>
                        {text.slice(0, middleIndex)}
                        <br />
                        {text.slice(middleIndex)}
                    </>
                );
            }

            if (slide.lang === "ko") {
                const middleIndex = 5;

                return (
                    <>
                        {text.slice(0, middleIndex)}
                        <br />
                        {text.slice(middleIndex)}
                    </>
                );
            }

            if (slide.lang === "pl" || slide.lang === "tr") {
                const words = text.split(" ");

                return (
                    <>
                        {words.slice(0, 3).join("\u00A0") +
                            " " +
                            words.slice(3).join("\u00A0")}
                    </>
                );
            }

            if (
                slide.lang === "pt" ||
                slide.lang === "ru" ||
                slide.lang === "es"
            ) {
                const words = text.split(" ");

                return (
                    <>
                        {words.slice(0, 2).join("\u00A0") +
                            " " +
                            words.slice(2).join("\u00A0")}
                    </>
                );
            }
        }

        if (id === "globalText.langMangoParadoxModifierDay4") {
            if (slide.lang === "ja") {
                const middleIndex = 5;

                return (
                    <>
                        {text.slice(0, middleIndex)}
                        <br />
                        {text.slice(middleIndex)}
                    </>
                );
            }

            if (slide.lang === "zh") {
                const middleIndex = 4;

                return (
                    <>
                        {text.slice(0, middleIndex)}
                        <br />
                        {text.slice(middleIndex)}
                    </>
                );
            }
        }

        if (id === "globalText.langMangoParadoxModifierDay6") {
            if (slide.lang === "en") {
                const words = text.split(" ");

                return <>{words.join("\u00A0")}</>;
            }
        }

        if (id === "globalText.langMangoParadoxModifierDay7") {
            if (slide.lang === "en") {
                const words = text.split(" ");

                return (
                    <>
                        {words[0]}
                        <br />
                        {words[1]}
                    </>
                );
            }

            if (slide.lang === "de") {
                const words = text.split("-");

                return (
                    <>
                        {words.slice(0, 2).join("-")}-
                        <br />
                        {words[2]}
                    </>
                );
            }

            if (slide.lang === "pt" || slide.lang === "es") {
                const words = text.split(" ");

                return (
                    <>
                        {words[0]} {words.slice(1).join("\u00A0")}
                    </>
                );
            }

            if (slide.lang === "tr") {
                const words = text.split(" ");

                return (
                    <>
                        {words.slice(0, 2).join("\u00A0")} {words[2]}
                    </>
                );
            }

            if (slide.lang === "ru") {
                const words = text.split(" ");

                return (
                    <>
                        {words[0]}
                        <br />
                        {words[1]}
                    </>
                );
            }
        }

        if (id === "globalText.langMangoParadoxModifierDay8") {
            if (slide.lang === "ja") {
                const middleIndex = 3;

                return (
                    <>
                        {text.slice(0, middleIndex)}
                        <br />
                        {text.slice(middleIndex)}
                    </>
                );
            }
        }

        if (id === "globalText.langMangoParadoxModifierDay9") {
            if (slide.lang === "ja") {
                const middleIndex = 4;

                return (
                    <>
                        {text.slice(0, middleIndex)}
                        <br />
                        {text.slice(middleIndex)}
                    </>
                );
            }
        }

        if (id === "globalText.langMangoParadoxModifierDay10") {
            if (
                slide.lang === "en" ||
                slide.lang === "fr" ||
                slide.lang === "it" ||
                slide.lang === "pt" ||
                slide.lang === "es"
            ) {
                const words = text.split(" ");

                return (
                    <>
                        {words[0]}&nbsp;{words.slice(1).join(" ")}
                    </>
                );
            }

            if (slide.lang === "ja") {
                const middleIndex = 4;

                return (
                    <>
                        {text.slice(0, middleIndex)}
                        <br />
                        {text.slice(middleIndex)}
                    </>
                );
            }
        }

        return text;
    };

    return (
        <>
            <div
                className={`slide progress-indicator ${styles.slide} ${
                    isVisible ? "visible" : ""
                } ${hasMileStoneLabels ? "with-milestone-labels" : ""} ${
                    targeting?.campaignId ?? ""
                } without-title ${startSequencePhase} ${slide.lang}`}
                ref={ref}
                style={barStyle}
            >
                {((dayNumber === 11 && startSequencePhase !== "end") ||
                    (dayNumber !== 11 && dayNumber !== 1)) && (
                    <div
                        className={classNames("previousRewardsPopover", {
                            isOpen: previousRewardsIsOpen,
                            isVisible: startSequencePhase === "end",
                        })}
                    >
                        <span className="previousRewardsPopoverRewardsLabel">
                            {translate("previousRewards", dayNumber)}
                        </span>

                        <div className="rewards">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(
                                (rewardDayNumber) => {
                                    const pickedRewardNumber =
                                        pickedRewards[rewardDayNumber];
                                    const rewards = allRewards[rewardDayNumber];
                                    const rewardUrl =
                                        rewards !== undefined &&
                                        pickedRewardNumber !== undefined
                                            ? rewards[pickedRewardNumber - 1]
                                            : undefined;

                                    const pickedSpellFactoryReward =
                                        pickedSpellFactoryRewards[
                                            rewardDayNumber
                                        ];

                                    return (
                                        <div
                                            className={`${rewardUrl !== undefined ? "earned" : "locked"} ${rewardDayNumber === dayNumber - 1 ? "latest" : ""}`}
                                            key={rewardDayNumber}
                                            style={{
                                                backgroundImage: rewardUrl
                                                    ? `url(${rewardUrl})`
                                                    : undefined,
                                            }}
                                            onClick={async () => {
                                                if (rewardUrl === undefined)
                                                    return;
                                                if (
                                                    pickedSpellFactoryReward !==
                                                    undefined
                                                ) {
                                                    localStorage.setItem(
                                                        `mango-paradox-spell-factory-reward-claimed-day-${dayNumber}`,
                                                        "true",
                                                    );

                                                    bridge().then(async (b) => {
                                                        b.claimVoucherQuiet(
                                                            pickedSpellFactoryReward,
                                                        );
                                                        b.close();
                                                    });
                                                    return;
                                                }
                                                (await bridge()).openShop();
                                            }}
                                        ></div>
                                    );
                                },
                            )}
                        </div>

                        <div
                            className="previousRewardsPopoverHandle"
                            onClick={togglePreviousRewardsIsOpen}
                        >
                            <div className="arrow"></div>
                        </div>
                    </div>
                )}
                {(() => {
                    const pickedRewardNumber = pickedRewards[dayNumber - 1];
                    const rewards = allRewards[dayNumber - 1];

                    const day10RewardUrl = relatedMedia?.find(
                        (media) => media.title === "reward-day-10",
                    )?.image?.url;

                    const rewardUrl =
                        dayNumber === 11 && day10RewardUrl !== undefined
                            ? day10RewardUrl
                            : rewards !== undefined &&
                                pickedRewardNumber !== undefined
                              ? rewards[pickedRewardNumber - 1]
                              : undefined;

                    if (rewardUrl === undefined) {
                        return null;
                    }

                    return (
                        <div className="previous-day-wrapper">
                            <div className="previous-day-text">
                                <div className={`title ${styles.title} `}>
                                    {translate("dayReward", dayNumber)}
                                </div>
                            </div>
                            <div className="previous-day-reward-wrapper">
                                <div
                                    className={`previous-day-reward ${pickedRewards[dayNumber - 1] === 0 ? "no-reward" : ""}`}
                                    style={{
                                        backgroundImage: `url(${rewardUrl})`,
                                    }}
                                ></div>
                            </div>
                            {previousDaySpellFactoryReward !== undefined && (
                                <Button
                                    onClick={() => {
                                        localStorage.setItem(
                                            `mango-paradox-spell-factory-reward-claimed-day-${dayNumber}`,
                                            "true",
                                        );

                                        bridge().then(async (b) => {
                                            b.claimVoucherQuiet(
                                                previousDaySpellFactoryReward,
                                            );
                                            b.close();
                                        });
                                    }}
                                    variant="green"
                                >
                                    <span className="buttonTextWrapper">
                                        <FormattedMessage id="globalText.langClaim" />
                                    </span>
                                </Button>
                            )}
                            <div className={`rays ${styles.rays}`}>
                                <div className={`ray ${styles.ray}`}></div>
                            </div>
                        </div>
                    );
                })()}
                <div
                    className={`background-layer ${styles["background-layer"]}`}
                ></div>
                {dayNumber !== 1 && (
                    <div
                        className={`previous-day-background-layer ${styles["previous-day-background-layer"]}`}
                    ></div>
                )}
                {timerString !== undefined && (
                    <div className="timer-container">
                        <div className="timer-label">
                            <FormattedMessage id="globalText.langMangoParadoxTimerLabel" />
                        </div>
                        <Timer
                            timerString={timerString}
                            lang={String(slide.lang)}
                        />
                    </div>
                )}
                {((dayNumber === 11 && startSequencePhase !== "end") ||
                    dayNumber !== 11) && (
                    <div className={`modifier modifier-${dayNumber}`}>
                        <div className="icon"></div>
                        <div className="text-wrapper">
                            <div className="text">
                                {startSequencePhase === "start" ||
                                startSequencePhase === "playing"
                                    ? formattedModifierText(
                                          `globalText.langMangoParadoxModifierDay${dayNumber - 1}`,
                                      )
                                    : formattedModifierText(
                                          `globalText.langMangoParadoxModifierDay${dayNumber}`,
                                      )}
                            </div>
                        </div>
                    </div>
                )}
                <div className={`content ${styles.content}`}>
                    {title && (
                        <>
                            <div className={`filter ${styles.filter}`} />

                            <div
                                className={`title ${styles.title} day-${
                                    startSequencePhase === "start" ||
                                    startSequencePhase === "playing"
                                        ? dayNumber - 1
                                        : dayNumber
                                } `}
                                data-title={title}
                            >
                                {(startSequencePhase !== "end" &&
                                    dayNumber === 11) ||
                                dayNumber !== 11 ? (
                                    <>
                                        #RandomBS -{" "}
                                        {startSequencePhase === "start" ||
                                        startSequencePhase === "playing"
                                            ? translate("day", dayNumber - 1)
                                            : translate("day", dayNumber)}
                                    </>
                                ) : (
                                    <>#RandomBS</>
                                )}
                            </div>
                        </>
                    )}
                    {(startSequencePhase !== "end" && dayNumber === 11) ||
                    dayNumber !== 11 ? (
                        <div className={`trackers ${styles.trackers}`}>
                            <div className={`rewards ${styles.rewards}`}>
                                <div
                                    className={`reward ${styles.reward}`}
                                ></div>
                                <div
                                    className={`reward ${styles.reward}`}
                                ></div>
                                <div
                                    className={`reward ${styles.reward}`}
                                ></div>
                            </div>
                            <div className={`rays ${styles.rays}`}>
                                <div className={`ray ${styles.ray}`}></div>
                                <div className={`ray ${styles.ray}`}></div>
                                <div className={`ray ${styles.ray}`}></div>
                            </div>
                            {isVisible && (
                                <>
                                    <div
                                        className={`milestone-label-wrapper ${styles["milestone-label-wrapper"]}`}
                                    >
                                        <div
                                            className={`milestone-label ${styles["milestone-label"]}`}
                                        >
                                            {startSequencePhase === "start" ||
                                            startSequencePhase === "playing" ? (
                                                <FormattedMessage
                                                    id={`globalText.langMangoParadoxGoalDay${dayNumber - 1}`}
                                                />
                                            ) : (
                                                <FormattedMessage
                                                    id={`globalText.langMangoParadoxGoalDay${dayNumber}`}
                                                />
                                            )}
                                        </div>
                                        {((startSequencePhase === "start" ||
                                            startSequencePhase === "playing") &&
                                        previousDaySlide !== undefined
                                            ? previousDaySlide.milestones
                                            : milestones
                                        ).map((milestone, index) => {
                                            if (!milestone.label) {
                                                return <div key={index} />;
                                            }

                                            const milestonePosition = Math.max(
                                                milestone.progress,
                                                5, // 5% is the minimum we support without the UI looking totally wack
                                            );

                                            const hasReachedMilestone =
                                                milestone.unlocked;

                                            const style = {
                                                left: `${milestonePosition}%`,
                                            };

                                            return (
                                                <div
                                                    key={index}
                                                    style={style}
                                                    className={`milestone-label ${
                                                        styles[
                                                            "milestone-label"
                                                        ]
                                                    } ${
                                                        hasReachedMilestone
                                                            ? styles[
                                                                  "milestone-reached"
                                                              ]
                                                            : styles[
                                                                  "milestone-unreached"
                                                              ]
                                                    } ${
                                                        hasReachedMilestone
                                                            ? "milestone-reached"
                                                            : "milestone-unreached"
                                                    }`}
                                                    title={milestone.label}
                                                >
                                                    {milestone.label}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="tracker-dice-wrapper">
                                        <div
                                            className={`tracker-wrapper day-${
                                                startSequencePhase === "end"
                                                    ? dayNumber
                                                    : dayNumber - 1
                                            } ${tracker.progress >= 100 ? "full" : ""}`}
                                        >
                                            <div
                                                className={`tracker day-${
                                                    startSequencePhase === "end"
                                                        ? dayNumber
                                                        : dayNumber - 1
                                                } ${styles.tracker} ${tracker.progress >= 100 ? "full" : ""}`}
                                            >
                                                <div
                                                    style={
                                                        startSequencePhase ===
                                                        "start"
                                                            ? previousDayStyle
                                                            : startSequencePhase ===
                                                                "playing"
                                                              ? midWayStyle
                                                              : style
                                                    }
                                                    className={`tracker-progress day-${dayNumber} ${
                                                        styles[
                                                            "tracker-progress"
                                                        ]
                                                    } ${
                                                        fillPercentage > 99
                                                            ? "almost-complete"
                                                            : ""
                                                    } ${
                                                        startSequencePhase ===
                                                        "start"
                                                            ? "previous-day"
                                                            : ""
                                                    }`}
                                                    ref={trackerProgressRef}
                                                >
                                                    {startSequencePhase ===
                                                        "end" && (
                                                        <RawValue
                                                            lang={slide.lang}
                                                            trackerProgress={
                                                                tracker.rawProgressInMillions
                                                            }
                                                            trackerProgressRef={
                                                                trackerProgressRef
                                                            }
                                                            key="current-day-raw-value"
                                                            dayNumber={
                                                                dayNumber
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                            <div
                                                className={`icon-container ${styles["icon-container"]}`}
                                                style={
                                                    startSequencePhase ===
                                                    "start"
                                                        ? previousDayStyle
                                                        : startSequencePhase ===
                                                            "playing"
                                                          ? midWayStyle
                                                          : iconStyle
                                                }
                                            >
                                                <div
                                                    className={`icon ${styles.icon} icon-${dayNumber}`}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    ) : (
                        <div
                            className={`trackers final-day ${styles.trackers}`}
                        >
                            <div className="final-rewards">
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(
                                    (rewardDayNumber) => {
                                        const rewardUrl = relatedMedia?.find(
                                            (media) => {
                                                return (
                                                    media.title ===
                                                    `reward-day-${rewardDayNumber}`
                                                );
                                            },
                                        )?.image?.url;

                                        return (
                                            <div
                                                className="final-reward"
                                                key={rewardDayNumber}
                                                style={{
                                                    backgroundImage: rewardUrl
                                                        ? `url(${rewardUrl})`
                                                        : undefined,
                                                }}
                                                onClick={async () => {
                                                    if (rewardUrl === undefined)
                                                        return;

                                                    const pickedSpellFactoryReward =
                                                        pickedSpellFactoryRewards[
                                                            rewardDayNumber
                                                        ];

                                                    if (
                                                        pickedSpellFactoryReward !==
                                                        undefined
                                                    ) {
                                                        localStorage.setItem(
                                                            `mango-paradox-spell-factory-reward-claimed-day-${rewardDayNumber}`,
                                                            "true",
                                                        );

                                                        bridge().then(
                                                            async (b) => {
                                                                b.claimVoucherQuiet(
                                                                    pickedSpellFactoryReward,
                                                                );
                                                                b.close();
                                                            },
                                                        );
                                                        return;
                                                    }
                                                    (await bridge()).openShop();
                                                }}
                                            ></div>
                                        );
                                    },
                                )}
                            </div>
                        </div>
                    )}
                    <div ref={bottomElementRef}></div>
                </div>
            </div>
            <div className={`wrapper ${styles.wrapper}`}>
                {ctas.length ? (
                    <div
                        className={`ctas ${
                            ctas.length === 1 ? "single" : "multi"
                        } ${styles.ctas} ${startSequencePhase}`}
                    >
                        {ctas.map(({ label, variant, onClick }, index) => {
                            const translateKey =
                                label === "Watch"
                                    ? "watch"
                                    : label === "More Info"
                                      ? "moreInfo"
                                      : undefined;

                            if (!translateKey) return null;

                            return (
                                <Button
                                    key={index}
                                    onClick={onClick}
                                    variant={variant}
                                >
                                    <span className="buttonTextWrapper">
                                        {translate(translateKey, dayNumber)}
                                    </span>
                                </Button>
                            );
                        })}
                    </div>
                ) : null}
            </div>
        </>
    );
};

function RawValue({
    lang,
    trackerProgress,
    trackerProgressRef,
    dayNumber,
}: {
    lang: string;
    trackerProgress?: number;
    trackerProgressRef: React.RefObject<HTMLDivElement | null>;
    dayNumber?: number;
}) {
    const ref = useRef<HTMLDivElement>(null);
    const [fitsLeft, setFitsLeft] = useState<boolean>(false);

    useEffect(() => {
        setTimeout(() => {
            if (!ref.current || !trackerProgressRef.current) return;

            setFitsLeft(
                ref.current.clientWidth + 20 <
                    trackerProgressRef.current.clientWidth,
            );
        }, 1100);

        setTimeout(() => {
            if (!ref.current || !trackerProgressRef.current) return;

            setFitsLeft(
                ref.current.clientWidth + 20 <
                    trackerProgressRef.current.clientWidth,
            );
        }, 2000);
    }, [trackerProgressRef.current, trackerProgress]);

    if (!trackerProgress) return null;

    const dailyAddRem = (() => {
        if (dayNumber === 2) return 0.3;
        if (dayNumber === 3) return 0.2;

        return 0;
    })();

    let { data: locale, error } = Locale.safeParse(lang);
    if (error || locale === undefined) {
        console.error(`Invalid locale passed to RawValue: ${lang}`);
        locale = "en";
    }

    return (
        <div
            className="rawValue"
            ref={ref}
            style={{
                left: !fitsLeft
                    ? `calc(100% + 1.4rem + ${dailyAddRem}rem)`
                    : "0.6rem",
            }}
        >
            <div className="rawValueText">
                {getLocalizedShortenedNumber(
                    trackerProgress * 1_000_000,
                    locale,
                )}
            </div>
        </div>
    );
}
